import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material'
import axios from 'axios'
import { useDropzone } from 'react-dropzone'
import { useEffect, useState } from 'react'
import type { ManufacturerRow } from '../ManufacturerList/ManufacturerList.types'
import type { SupplierRow } from './SupplierList.types'

export interface AssignOemDialogProps {
  open: boolean
  loading: boolean
  onClose: () => void
  onSubmit: (supplier: SupplierRow, manufacturer: ManufacturerRow, loaFile: File | null) => void
  manufacturerOptions: ManufacturerRow[]
  defaultSupplierId?: string | null
}

export function AssignOemDialog({
  open,
  loading,
  onClose,
  onSubmit,
  manufacturerOptions,
  defaultSupplierId,
}: AssignOemDialogProps) {
  const [supplierOptions, setSupplierOptions] = useState<SupplierRow[]>([])
  const [supplier, setSupplier] = useState<SupplierRow | null>(null)
  const [manufacturer, setManufacturer] = useState<ManufacturerRow | null>(null)
  const [loaFile, setLoaFile] = useState<File | null>(null)
  const {
    getRootProps,
    getInputProps,
    open: openFilePicker,
  } = useDropzone({
    noClick: true,
    multiple: false,
    onDropAccepted: files => setLoaFile(files[0]),
  })

  useEffect(() => {
    if (!open) return
    setSupplier(null)
    setManufacturer(null)
    setLoaFile(null)

    axios
      .get<{ count: number; list: SupplierRow[] }>('/supplier', { params: { status: 'approved', pageSize: 1000 } })
      .then(res => {
        setSupplierOptions(res.data.list)
        setSupplier(res.data.list.find(s => s.id === defaultSupplierId) ?? null)
      })
      .catch(err => console.error('Failed to load suppliers', err))
  }, [open, defaultSupplierId])

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle fontWeight={600}>Request an OEM to be Assigned to an Approved Supplier</DialogTitle>

      <DialogContent>
        <Typography variant="body2" color="text.secondary" marginBottom={2}>
          Please select an approved supplier and the requested OEM.
        </Typography>

        <Box display="flex" flexDirection="column" gap={2}>
          <Autocomplete
            size="small"
            options={supplierOptions}
            getOptionLabel={o => o.name}
            value={supplier}
            onChange={(_e, newValue) => setSupplier(newValue)}
            renderInput={params => <TextField {...params} label="Supplier" />}
          />

          <Autocomplete
            size="small"
            options={manufacturerOptions}
            getOptionLabel={o => o.supplier}
            value={manufacturer}
            onChange={(_e, newValue) => setManufacturer(newValue)}
            renderInput={params => <TextField {...params} label="Requested OEM" />}
          />

          <Box
            {...getRootProps()}
            border="2px dashed rgba(0,0,0,0.2)"
            borderRadius={1}
            padding={3}
            textAlign="center"
            display="flex"
            flexDirection="column"
            gap={1}
          >
            <input {...getInputProps()} />
            <Typography fontWeight={600}>Drop LOA to Upload (Optional)</Typography>
            <Typography variant="body2" color="text.secondary">
              or
            </Typography>
            <Box>
              <Button variant="contained" size="small" onClick={openFilePicker}>
                CHOOSE FILE
              </Button>
            </Box>
          </Box>

          {loaFile && (
            <Box>
              <Chip label={loaFile.name} size="small" onDelete={() => setLoaFile(null)} />
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button variant="outlined" onClick={onClose}>
          CANCEL
        </Button>
        <Button
          variant="contained"
          disabled={!supplier || !manufacturer}
          onClick={() => {
            if (supplier && manufacturer) onSubmit(supplier, manufacturer, loaFile)
          }}
          loading={loading}
        >
          SUBMIT
        </Button>
      </DialogActions>
    </Dialog>
  )
}
